/**
 * DefaultFeeSection Component
 * 협력사 기본 수수료 설정 섹션 (신규 라이더 자동 적용용)
 * 아코디언 형태로 접혔다 펼 수 있음
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { supplierDefaultFeeApi, DefaultFeeDto, SaveDefaultFeeRequest } from '../api/SupplierDefaultFeeApi';
import { COMMISSION_TYPE_OPTIONS } from '../lib/supplierFeeHelpers';
import { formatCurrency } from '@/shared/utils/formatter';
import '@/shared/styles/common-table.css';

interface DefaultFeeSectionProps {
    selectedSupplierId?: string;
    isReadOnly: boolean;
}

interface DefaultFeeRow extends DefaultFeeDto {
    isEdited: boolean;
    isNew: boolean;
    commissionType: string;
}

const DefaultFeeSection: React.FC<DefaultFeeSectionProps> = ({ selectedSupplierId, isReadOnly }) => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();
    const [loading, setLoading] = useState<boolean>(true);
    const [defaultFees, setDefaultFees] = useState<DefaultFeeRow[]>([]);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const fetchDefaultFees = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = getUserInfo();
            if (!userInfo) {
                setLoading(false);
                return;
            }

            const targetSupplierId = selectedSupplierId || userInfo.suppId;
            if (!targetSupplierId) {
                showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
                setLoading(false);
                return;
            }

            const fees = await supplierDefaultFeeApi.getDefaultFees(targetSupplierId);
            const feeRows: DefaultFeeRow[] = fees.map((fee) => {
                let commissionType = 'PER_CASE_FIXED';
                if (fee.calcType === '1') {
                    commissionType = fee.settlementPeriod === 'DAILY' ? 'PER_DAY_FIXED' : 'PER_CASE_FIXED';
                } else if (fee.calcType === '2') {
                    commissionType = fee.settlementPeriod === 'DAILY' ? 'PER_DAY_RATE' : 'PER_CASE_RATE';
                }
                return { ...fee, commissionType, isEdited: false, isNew: false };
            });

            setDefaultFees(feeRows);
            setLoading(false);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setDefaultFees([]);
                setLoading(false);
                return;
            }
            if (!sessionStorage.getItem('default-fee-error-shown')) {
                showToast('기본 수수료를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                sessionStorage.setItem('default-fee-error-shown', 'true');
            }
            setDefaultFees([]);
            setLoading(false);
        }
    }, [selectedSupplierId]);

    useEffect(() => {
        fetchDefaultFees();
    }, [fetchDefaultFees]);

    const handleCommissionTypeChange = (index: number, value: string) => {
        const updated = [...defaultFees];
        updated[index].commissionType = value;
        updated[index].isEdited = true;

        if (value === 'PER_CASE_FIXED') {
            updated[index].calcType = '1';
            updated[index].settlementPeriod = 'WEEKLY';
            updated[index].feeRate = null;
        } else if (value === 'PER_CASE_RATE') {
            updated[index].calcType = '2';
            updated[index].settlementPeriod = 'WEEKLY';
            updated[index].feeAmt = null;
        } else if (value === 'PER_DAY_FIXED') {
            updated[index].calcType = '1';
            updated[index].settlementPeriod = 'DAILY';
            updated[index].feeRate = null;
        } else if (value === 'PER_DAY_RATE') {
            updated[index].calcType = '2';
            updated[index].settlementPeriod = 'DAILY';
            updated[index].feeAmt = null;
        }
        setDefaultFees(updated);
    };

    const handleAmountChange = (index: number, value: string) => {
        const updated = [...defaultFees];
        const numValue = value === '' ? null : Number(value);
        const commissionType = updated[index].commissionType;

        if (commissionType === 'PER_CASE_FIXED' || commissionType === 'PER_DAY_FIXED') {
            updated[index].feeAmt = numValue;
            updated[index].feeRate = null;
        } else {
            updated[index].feeRate = numValue;
            updated[index].feeAmt = null;
        }
        updated[index].isEdited = true;
        setDefaultFees(updated);
    };

    const handleSave = async (index: number) => {
        const fee = defaultFees[index];
        const userInfo = getUserInfo();

        if (!userInfo) {
            showToast('사용자 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        const targetSupplierId = selectedSupplierId || userInfo.suppId;
        if (!targetSupplierId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        if (!fee.feeName || fee.feeName.trim() === '') {
            showToast('수수료명을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        if (!fee.commissionType) {
            showToast('수수료구분을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        const isFixed = fee.commissionType === 'PER_CASE_FIXED' || fee.commissionType === 'PER_DAY_FIXED';

        if (isFixed && (fee.feeAmt === null || fee.feeAmt <= 0)) {
            showToast('금액은 0보다 큰 값을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        if (!isFixed && (fee.feeRate === null || fee.feeRate <= 0 || fee.feeRate > 100)) {
            showToast('정률은 0보다 크고 100 이하의 값을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        try {
            const request: SaveDefaultFeeRequest = {
                suppId: targetSupplierId,
                feeCode: fee.feeCode || `DEFAULT_${Date.now()}`,
                feeName: fee.feeName,
                calcType: fee.calcType,
                feeAmt: fee.feeAmt,
                feeRate: fee.feeRate,
                settlementPeriod: fee.settlementPeriod,
                startDay: fee.startDay,
                endDay: fee.endDay,
                userId: userInfo.userId,
            };

            await supplierDefaultFeeApi.saveDefaultFee(request);
            showToast('기본 수수료가 저장되었습니다.', { type: ToastType.SUCCESS });
            await fetchDefaultFees();
            setShowAddForm(false);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                showToast('기본 수수료 API가 아직 구현되지 않았습니다.', { type: ToastType.WARNING });
                return;
            }
            showToast('기본 수수료 저장에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    const handleAddNew = () => {
        const newFee: DefaultFeeRow = {
            feeCode: '',
            feeName: '',
            feeType: 'S',
            calcType: '1',
            feeAmt: null,
            feeRate: null,
            settlementPeriod: 'WEEKLY',
            startDay: new Date().toISOString().split('T')[0],
            endDay: '9999-12-31',
            useYn: 'Y',
            commissionType: '',
            isEdited: true,
            isNew: true,
        };
        setDefaultFees([...defaultFees, newFee]);
        setShowAddForm(true);
    };

    if (loading) {
        return (
            <div style={{ padding: '20px', backgroundColor: 'var(--color-white)', borderRadius: 'var(--border-radius-lg)', marginBottom: '24px', border: '1px solid var(--color-gray-200)' }}>
                <p style={{ textAlign: 'center', color: 'var(--color-gray-600)' }}>기본 수수료 정보를 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: '24px' }}>
            {/* 아코디언 헤더 */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(to bottom, #f0fdf4, #dcfce7)',
                    border: '1px solid #d8ffe5',
                    borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(5, 150, 105, 0.08)',
                    userSelect: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#28a745', fontSize: '0.875rem' }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#28a745' }}>기본 수수료 설정</span>
                    {defaultFees.length > 0 && (
                        <span style={{ fontSize: '0.875rem', color: '#495057', fontWeight: '500' }}>
                            (현재 적용: {COMMISSION_TYPE_OPTIONS.find((opt) => opt.value === defaultFees[0].commissionType)?.label || '건당 정액'} /
                            <span style={{ color: '#04B374', fontWeight: '600' }}>
                                {defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED'
                                    ? ` ${defaultFees[0].feeAmt || 0}원`
                                    : ` ${defaultFees[0].feeRate || 0}%`}
                            </span>)
                        </span>
                    )}
                </div>
                <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{isExpanded ? '접기' : '펼치기'}</span>
            </div>

            {/* 아코디언 내용 */}
            {isExpanded && (
                <div style={{ background: '#ffffff', border: '1px solid #d8ffe5', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '20px', boxShadow: '0 1px 3px rgba(5, 150, 105, 0.08)' }}>
                    {defaultFees.length > 0 && !isEditMode ? (
                        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '16px' }}>
                            <div style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 12px', borderRadius: '16px', fontSize: '0.875rem', fontWeight: '500', marginRight: '16px' }}>현재 적용 중</div>
                            <span style={{ fontSize: '0.875rem', color: '#495057', fontWeight: '500' }}>
                                수수료구분 : <span style={{ fontWeight: '600' }}>{COMMISSION_TYPE_OPTIONS.find((opt) => opt.value === defaultFees[0].commissionType)?.label || '건당 정액'}</span>
                                &nbsp;&nbsp; 금액 : <span style={{ color: '#04B374', fontWeight: '600' }}>
                                    {defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED'
                                        ? `${defaultFees[0].feeAmt || 0} 원`
                                        : `${defaultFees[0].feeRate || 0} %`}
                                </span>
                                &nbsp;&nbsp; 적용일 : <span style={{ fontWeight: '600' }}>{defaultFees[0].startDay} ~</span>
                            </span>
                            <div style={{ marginLeft: 'auto' }}>
                                {!isReadOnly && (
                                    <button onClick={(e) => { e.stopPropagation(); setIsEditMode(true); }} style={{ backgroundColor: '#ffffff', color: '#6c757d', border: '1px solid #dee2e6', padding: '6px 12px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer' }}>
                                        수수료 변경
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 12px', borderRadius: '16px', fontSize: '0.875rem', fontWeight: '500', marginRight: '12px' }}>
                                    {defaultFees.length > 0 ? '수정 모드' : '신규 설정'}
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#6c757d', fontWeight: '500' }}>
                                    {defaultFees.length > 0 ? '기본 수수료를 수정해주세요' : '기본 수수료를 설정해주세요'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <span style={{ minWidth: '80px', fontSize: '0.875rem', fontWeight: '500', color: '#495057' }}>수수료구분 :</span>
                                <select
                                    value={defaultFees.length > 0 ? defaultFees[0].commissionType : 'PER_CASE_FIXED'}
                                    onChange={(e) => {
                                        if (defaultFees.length > 0) {
                                            handleCommissionTypeChange(0, e.target.value);
                                        } else {
                                            handleAddNew();
                                            setTimeout(() => handleCommissionTypeChange(0, e.target.value), 0);
                                        }
                                    }}
                                    disabled={isReadOnly}
                                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem', minWidth: '120px' }}
                                >
                                    {COMMISSION_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#495057' }}>금액 :</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                        type="text"
                                        value={defaultFees.length > 0
                                            ? (defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED'
                                                ? (defaultFees[0].feeAmt ? formatCurrency(defaultFees[0].feeAmt.toString()) : '')
                                                : (defaultFees[0].feeRate ? defaultFees[0].feeRate.toString() : ''))
                                            : ''}
                                        onChange={(e) => {
                                            const isFixed = defaultFees.length > 0 ? (defaultFees[0].commissionType === 'PER_CASE_FIXED' || defaultFees[0].commissionType === 'PER_DAY_FIXED') : true;
                                            const value = isFixed ? e.target.value.replace(/[^0-9]/g, '') : e.target.value.replace(/[^0-9.]/g, '');
                                            if (defaultFees.length > 0) {
                                                handleAmountChange(0, value);
                                            } else {
                                                handleAddNew();
                                                setTimeout(() => handleAmountChange(0, value), 0);
                                            }
                                        }}
                                        placeholder="금액"
                                        disabled={isReadOnly}
                                        style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem', width: '80px', textAlign: 'right' }}
                                    />
                                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                        {defaultFees.length > 0 && (defaultFees[0].commissionType === 'PER_CASE_RATE' || defaultFees[0].commissionType === 'PER_DAY_RATE') ? '%' : '원'}
                                    </span>
                                </div>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#495057' }}>적용일 :</span>
                                <input
                                    type="date"
                                    value={defaultFees.length > 0 ? defaultFees[0].startDay : new Date().toISOString().split('T')[0]}
                                    onChange={(e) => {
                                        if (defaultFees.length > 0) {
                                            const updated = [...defaultFees];
                                            updated[0].startDay = e.target.value;
                                            updated[0].isEdited = true;
                                            setDefaultFees(updated);
                                        }
                                    }}
                                    disabled={isReadOnly}
                                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem' }}
                                />
                                <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>~</span>
                                {!isReadOnly && (
                                    <button
                                        onClick={async () => {
                                            if (defaultFees.length > 0) {
                                                await handleSave(0);
                                                setIsEditMode(false);
                                            } else {
                                                if (!showAddForm) handleAddNew();
                                                setTimeout(async () => { await handleSave(0); setIsEditMode(false); }, 0);
                                            }
                                        }}
                                        style={{ backgroundColor: '#05CD82', color: 'white', border: 'none', padding: '6px 16px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', marginLeft: '8px' }}
                                    >
                                        저장
                                    </button>
                                )}
                                {isEditMode && (
                                    <button
                                        onClick={() => { setIsEditMode(false); fetchDefaultFees(); }}
                                        style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '6px 16px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer', marginLeft: '8px' }}
                                    >
                                        취소
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <div style={{ padding: '12px 16px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px', fontSize: '0.875rem', color: '#1976d2' }}>
                        <i className="fas fa-lightbulb" style={{ marginRight: '8px' }}></i>
                        <strong>TIP:</strong> 여기서 설정한 기본 수수료는 신규 라이더가 회원가입할 때 자동으로 적용됩니다.
                    </div>
                </div>
            )}
        </div>
    );
};

export default DefaultFeeSection;
